import axios from 'axios';
import { API_STATUS } from '../constants/apiConstants';
import { API_URLS } from '../constants/apiUrls';
import { useAuthStore } from '../store/useAuthStore';
import { forceLogout } from './sessionLogout';

// Only our own API speaks for the session; a 401 from any other host the
// global axios reaches (third-party services) says nothing about it.
const OWN_API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
// 401s that must not end the session, matched by URL prefix (the URL may
// carry a query string): login and refresh failures are handled by their
// callers (the auth guard and the session renewers); banner, push
// notification and pause calls are background extras that already fall back
// on failure.
const NON_SESSION_URLS = [
  API_URLS.LOGIN,
  API_URLS.REFRESH,
  API_URLS.BANNER,
  API_URLS.PAUSE_NOTIFICATIONS,
  API_URLS.FCM_SAVE,
  API_URLS.FCM_DELETE,
  API_URLS.FCM_GET,
  API_URLS.FCM_EXISTS,
];

// Prefix match against a URL template; {0}, {1}... stand for one path segment.
// Anchored at both ends: the template must line up with a path/query boundary
// so a future endpoint whose path only starts with an excluded template
// (/api/v2/auth/refresh-status next to /api/v2/auth/refresh) is not treated
// as the excluded one.
const matchesUrlTemplate = (template, url) => {
  if (!template) return false;
  const pattern = template
    .replace(/[.*+?^$()|[\]\\]/g, '\\$&')
    .replace(/\{\d+\}/g, '[^/?]+');
  return new RegExp(`^${pattern}($|[/?])`).test(url);
};

const getRequestToken = (config) => {
  const header = config?.headers?.Authorization ?? config?.headers?.get?.('Authorization');
  return typeof header === 'string' ? header.split(' ').pop() : null;
};

// A request sent with an older token (in flight while the session renewed)
// may be rejected without saying anything about the current one.
const carriesCurrentToken = (config) => {
  const requestToken = getRequestToken(config);
  return Boolean(requestToken) && requestToken === useAuthStore.getState().authData?.accessToken;
};

const rejectsSessionToken = (error) => {
  const url = error?.config?.url || '';
  return (
    error?.response?.status === API_STATUS.UNAUTHORIZED &&
    Boolean(OWN_API_BASE_URL) &&
    url.startsWith(OWN_API_BASE_URL) &&
    !NON_SESSION_URLS.some((template) => matchesUrlTemplate(template, url)) &&
    carriesCurrentToken(error.config)
  );
};

let isInstalled = false;

// Any other call to our API rejecting the current access token with a 401
// means it is no longer accepted, even if its expiry has not passed yet
// (revoked, server clock ahead). Log the session out instead of carrying on
// with a dead token.
export const installSessionInterceptor = () => {
  if (isInstalled) return;
  isInstalled = true;
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (rejectsSessionToken(error)) forceLogout();
      return Promise.reject(error);
    }
  );
};
